import { useMutation } from '@apollo/client';
import type { OperationVariables } from '@apollo/client/core';
import type { MutationHookOptions, MutationResult } from '@apollo/client/react/types/types';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { DocumentNode } from 'graphql';

import { useEvent } from 'common/components/hooks/useEvent/useEvent';
import { TrackedError } from 'ErrorBoundary/Error';

export type UseMutationSubmitCallbackVariablesFn<TValues, TVariables> = (values: TValues) => TVariables;
export type UseMutationSubmitCallbackVariables<TValues, TVariables> =
  | TVariables
  | UseMutationSubmitCallbackVariablesFn<TValues, TVariables>;

export type UseMutationSubmitCallbackConfig<TValues = unknown, TData = unknown, TVariables = OperationVariables> = {
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>;
  mutationOptions?: MutationHookOptions<TData, TVariables>;
  variables: UseMutationSubmitCallbackVariables<TValues, TVariables>;
  onSuccess?: (data?: TData | null) => Promise<void> | void;
  errorMessage?: string;
  onError?: (e: Error) => void;
};

export type UseMutationSubmitCallbackOutput<TValues, TData> = {
  mutationResult: MutationResult<TData>;
  onSubmit: (values?: TValues) => Promise<void>;
};

export function useMutationSubmitCallback<TValues = unknown, TData = unknown, TVariables = OperationVariables>({
  mutation,
  mutationOptions,
  variables: variablesProp,
  onSuccess,
  errorMessage,
  onError,
}: UseMutationSubmitCallbackConfig<TValues, TData, TVariables>): UseMutationSubmitCallbackOutput<TValues, TData> {
  const [mutationFn, mutationResult] = useMutation<TData, TVariables>(mutation, mutationOptions);

  const onSubmit = useEvent(async (values: TValues) => {
    const variables = isVariablesFunction(variablesProp) ? variablesProp(values) : variablesProp;

    try {
      const { data } = await mutationFn({
        variables,
      });

      await onSuccess?.(data);
    } catch (e) {
      if (!onError) {
        throw new TrackedError({
          source: e,
          content: errorMessage,
        });
      }

      onError(e);
    }
  });

  return {
    mutationResult,
    onSubmit,
  };
}

function isVariablesFunction<TValues, TVariables>(
  test: TVariables | ((values: TValues) => TVariables)
): test is (values: TValues) => TVariables {
  return typeof test === 'function';
}
